import { StepContentProps } from './types';
import DateForm from './forms/DateForm';
import UserForm from './forms/UserForm';
import ErrorStep from './forms/ErrorStep';
import ProviderForm from './forms/ProviderForm';
import LocationForm from './forms/LocationForm';
import ServiceForm from './forms/service-step/ServiceForm';
import WizardFinal from './WizardFinal';
import PaymentForm from './forms/PaymentForm/PaymentForm';
import { useStepContext } from '../steps-context/StepsContext';
import usePaymentGateways from '../../../hooks/use-payment-gateways';
import WidgetSurveyForm from '../../../ui-component/widgets/WidgetSurveyForm';
import { useMemo } from 'react';

const StepContent = ({
    step,
    addProgress,
    substituteProgress,
    errorIndex,
    handleNext,
    handleBack,
    setErrorIndex,
    services,
    serviceData,
    setServiceData,
    locationData,
    setLocationData,
    providerData,
    setProviderData,
    dateData,
    setDateData,
    userData,
    setUserData,
    attachments,
    setAttachments,
    attachmentsIdsToDelete,
    setAttachmentsIdsToDelete,
    isAnyProvider,
    setIsAnyProvider,
    isMobile,
    submitBooking,
    resetWidget,
    rawResetWidget,
    submitted,
    data,
    matchDownMd,
    setHideNav,
    timezone,
    setTimezone,
    widgetAnswers,
    setWidgetAnswers,
    selectedOptionIds,
    clickOptionId
}: StepContentProps) => {
    const { steps } = useStepContext();
    const gateways = usePaymentGateways(data, providerData);

    const successUrl = useMemo(() => {
        if (serviceData.length) {
            return serviceData.map(({ custom_success_url }) => custom_success_url).filter((v) => !!v)[0] ?? null;
        }
        return null;
    }, [serviceData]);

    switch (step) {
        case steps.service.index:
            return (
                <ServiceForm
                    step={step}
                    handleBack={handleBack}
                    addProgress={addProgress}
                    substituteProgress={substituteProgress}
                    handleNext={handleNext}
                    services={services}
                    serviceData={serviceData}
                    setServiceData={setServiceData}
                    setLocationData={setLocationData}
                    setProviderData={setProviderData}
                    setDateData={setDateData}
                    matchDownMd={matchDownMd}
                    setHideNav={setHideNav}
                    submitted={submitted}
                    useMultiservices={data?.settings.widget.use_multiservices}
                    selectedOptionIds={selectedOptionIds}
                    clickOptionId={clickOptionId}
                />
            );
        case steps.location.index:
            return (
                <LocationForm
                    step={step}
                    handleBack={handleBack}
                    addProgress={addProgress}
                    substituteProgress={substituteProgress}
                    handleNext={handleNext}
                    setErrorIndex={setErrorIndex}
                    serviceData={serviceData}
                    locationData={locationData}
                    setLocationData={setLocationData}
                    setProviderData={setProviderData}
                    setDateData={setDateData}
                    filteredLocations={data?.filteredLocations}
                    submitted={submitted}
                />
            );

        case steps.provider?.index:
            return (
                <ProviderForm
                    step={step}
                    handleBack={handleBack}
                    addProgress={addProgress}
                    substituteProgress={substituteProgress}
                    serviceData={serviceData}
                    locationData={locationData}
                    providerData={providerData}
                    setProviderData={setProviderData}
                    setDateData={setDateData}
                    setErrorIndex={setErrorIndex}
                    handleNext={handleNext}
                    isAnyProvider={isAnyProvider}
                    setIsAnyProvider={setIsAnyProvider}
                    filteredEmployees={data?.filteredEmployees}
                    submitted={submitted}
                />
            );
        case steps.date.index:
            return (
                <DateForm
                    step={step}
                    handleBack={handleBack}
                    addProgress={addProgress}
                    substituteProgress={substituteProgress}
                    company={data}
                    serviceData={serviceData}
                    locationData={locationData}
                    providerData={providerData}
                    setProviderData={setProviderData}
                    dateData={dateData}
                    setDateData={setDateData}
                    errorIndex={errorIndex}
                    handleNext={handleNext}
                    isAnyProvider={isAnyProvider}
                    setIsAnyProvider={setIsAnyProvider}
                    submitted={submitted}
                    timezone={timezone}
                    setTimezone={setTimezone}
                    selectedOptionIds={selectedOptionIds}
                />
            );

        case steps?.survey?.index:
            return (
                <WidgetSurveyForm
                    formId={`widget-form-${steps?.survey?.index}`}
                    data={widgetAnswers ?? []}
                    onSubmit={(v) => {
                        setWidgetAnswers(v);
                        handleNext();
                    }}
                />
            );

        case steps.user.index:
            return (
                <UserForm
                    step={step}
                    handleBack={handleBack}
                    addProgress={addProgress}
                    substituteProgress={substituteProgress}
                    handleNext={handleNext}
                    userData={userData}
                    setUserData={setUserData}
                    attachments={attachments}
                    setAttachments={setAttachments}
                    attachmentsIdsToDelete={attachmentsIdsToDelete}
                    setAttachmentsIdsToDelete={setAttachmentsIdsToDelete}
                    isMobile={isMobile}
                    submitted={submitted}
                    company={data}
                    service={serviceData}
                    submitBooking={submitBooking}
                    location_id={locationData?.id}
                    providerData={providerData}
                    widget_answers={widgetAnswers}
                />
            );

        case steps?.payment?.index:
            return (
                <PaymentForm
                    service={serviceData}
                    submitted={submitted}
                    handleBack={handleBack}
                    step={step}
                    addProgress={addProgress}
                    substituteProgress={substituteProgress}
                    handleNext={handleNext}
                    company={data}
                    date={dateData}
                    location={locationData}
                    provider={providerData}
                    user={userData}
                    submitBooking={submitBooking}
                    gateways={gateways}
                    timezone={timezone}
                    attachments={attachments}
                />
            );
        case steps.final.index:
            return (
                <WizardFinal
                    step={step}
                    handleBack={handleBack}
                    submitBooking={submitBooking}
                    resetWidget={resetWidget}
                    rawResetWidget={rawResetWidget}
                    submitted={submitted}
                    waitlist={data.settings.appointments.waitlist}
                    success_url={successUrl}
                />
            );
        default:
            return <ErrorStep />;
    }
};

export default StepContent;
